<script setup lang="ts">

import * as sr_opt from '../fin/single_retirement_optimisation';
import RetirementConfigElem from './RetirementConfigElem.vue';
import SingleRetirementOutput from './SingleRetirementOutput.vue';
import { TimeSeriesS } from '../fin/history';
import { Direction } from '../fin/optimisation';
import { RetirementConfig } from '../fin/retirement_config';
import { RetirementState } from '../fin/retirement_state';
import { ref, Ref, shallowRef, } from 'vue';
import { simulate_single_history } from '../fin/retirement';

interface Loadable {
  load(): void;
}

const retirementConfigElemRef = ref<Loadable|null>(null);
const birthYear = ref<number>(0);
const assetTimeSeries = shallowRef(new TimeSeriesS<number>([]));
const flowTimeSeries = shallowRef(new TimeSeriesS<number>([]));
const retirementYear: Ref<number> = ref<number>(0);
const exhausted: Ref<string> = ref<string>('');
const exhaustionYear: Ref<string> = ref<string>('');
const exhaustionAge: Ref<string> = ref<string>('');
const maxYear: Ref<number> = ref<number>(0);
const earlyPensionWithdrawalGuidance = ref<Direction>(Direction.NO_GUIDANCE);
const postRetirementIsaSavingGuidance = ref<Direction>(Direction.NO_GUIDANCE);
const emit = defineEmits(['configChanged']);

const directionalDerivativeEarlyPension = sr_opt.directionalGuidanceFactory(
  cfg => cfg.strategy.early_pension_withdrawal_pa,
  (cfg, input) => {
    const copy = cfg.clone();
    copy.strategy.early_pension_withdrawal_pa = input;
    return copy;
  },
  reallyCalculate,
  0,  // lower input limit
  500_000,  // upper input limit
  20,  // sample count
  5_000,  // input interval
);

const directionalDerivativePostRetirementIsaSaving = sr_opt.directionalGuidanceFactory(
  cfg => cfg.strategy.postRetirementISASavingRatePa,
  (cfg, input) => {
    const copy = cfg.clone();
    copy.strategy.postRetirementISASavingRatePa = input;
    return copy;
  },
  reallyCalculate,
  0,  // lower input limit
  20_000,  // upper input limit
  20,  // sample count
  1_000,  // input interval
);

function reallyCalculate(config: RetirementConfig) {
  config = config.zeroVariances();

  const pre_retirement_rs: RetirementState = new RetirementState(
    config.person.simulation_start_year,
    config.person,
    config.initialAssets,
    config.market,
    [],
  );

  const history = simulate_single_history(
    pre_retirement_rs,
    config.strategy,
    [],  // default flow strategies
  );

  return history;
}

function calculate(config: RetirementConfig): void {
  console.log('SingleRetirement calculate starting');
  retirementConfigElemRef.value?.load();
  const history = reallyCalculate(config);
  earlyPensionWithdrawalGuidance.value = directionalDerivativeEarlyPension(config);
  postRetirementIsaSavingGuidance.value = directionalDerivativePostRetirementIsaSaving(config);
  const retirementAge: number = config.person.retirement_age;
  birthYear.value = config.person.birth_date.getUTCFullYear();

  // put the data into the props for the plot components
  assetTimeSeries.value = history.asset_history;
  flowTimeSeries.value = history.flow_history;
  retirementYear.value = birthYear.value + retirementAge;

  if (history.exhausted) {
    exhausted.value = "yes";
    exhaustionYear.value = '' + history.exhaustion_year;
    exhaustionAge.value = '' + (history.exhaustion_year - birthYear.value);
  } else {
    exhausted.value = "no";
    exhaustionYear.value = '';
    exhaustionAge.value = '';
  }

  maxYear.value = history.max_year;
  console.log('SingleRetirement calculate complete');
}

defineExpose({calculate});

</script>

<template>
  <div activityName="SingleRetirement">
    <h2>Simple Single Retirement Simulation</h2>
    <div class="explainer-card">
      Simulate a single retirement, with predictable/deterministic investment
      returns chosen by you.
      <br/>
      If you want to explore uncertainty then move on to the next page which
      runs many (hundreds or thousands) of similar retirement scenarios with
      the same starting conditions, but randomly varying investment returns
      and reveals the ranges of possible outcomes.
      <br/>
      You can save a CSV file of each plot's data to your download folder by
      right clicking on the legend and then selecting the CSV button.
    </div>
    <RetirementConfigElem
      ref="retirementConfigElemRef"
      :quantileKeys="[]"
      @configChanged="(cfg) => {calculate(cfg); emit('configChanged', cfg);}"
      :showMarketUncertainty="false"
      :earlyPensionWithdrawalGuidance="earlyPensionWithdrawalGuidance"
      :postRetirementIsaSavingGuidance="postRetirementIsaSavingGuidance"
    />
    <SingleRetirementOutput
      :assetTimeSeries="assetTimeSeries"
      :flowTimeSeries="flowTimeSeries"
      :birthYear="birthYear"
      :retirementYear="retirementYear"
      :exhausted="exhausted"
      :exhaustionYear="exhaustionYear"
      :exhaustionAge="exhaustionAge"
      :maxYear="maxYear"
    />
  </div>
</template>

<style scoped>
</style>
